import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./Progress.css";
import {
  Book,
  Clock,
  Check,
  ArrowRight2,
  DocumentText,
  VideoPlay,
  Archive,
  Eye,
} from "iconsax-reactjs";
import { fetchUserProgress } from "../../../redux/slices/progressSlice";
import { fetchUserEnrolledCohorts } from "../../../redux/slices/userEnrolledCohortSlice";
import { fetchRecapMaterials } from "../../../redux/slices/classRecapMaterialSlice";
import { fetchAssignments } from "../../../redux/slices/assignmentSlice";
import { useFetchUser } from "../../../utils/useUserStore";
import { deduplicateEnrollments } from "../../../utils/enrollmentUtils";

function Progress() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useFetchUser();

  const isOrg =
    user?.account_type === "organization_member" ||
    window.location.pathname.startsWith("/organization");
  const routePrefix = isOrg ? "/organization" : "";

  // Redux state
  const {
    progressData,
    loading: progressLoading,
    error: progressError,
    pendingLoading,
    completedLoading,
  } = useSelector((state) => state.progress);
  const userEnrolledCohorts = useSelector(
    (state) => state.userEnrolledCohorts?.enrollment || []
  );
  const recapMaterials = useSelector(
    (state) => state.classRecapMaterials?.recapMaterials || []
  );
  const userAssignments = useSelector(
    (state) => state.userAssignments?.assignments || []
  );

  const [activeTab, setActiveTab] = useState("overview");
  const [selectedCourseFilter, setSelectedCourseFilter] = useState("all");

  useEffect(() => {
    dispatch(fetchUserProgress());
    dispatch(fetchUserEnrolledCohorts());
    dispatch(fetchRecapMaterials());
    dispatch(fetchAssignments());
  }, [dispatch]);

  // Combine and deduplicate enrolled courses
  const enrollments = useMemo(() => {
    return deduplicateEnrollments(userEnrolledCohorts, progressData);
  }, [userEnrolledCohorts, progressData]);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Helper function to determine resource type
  const getResourceType = (filePath, title = "") => {
    const combined = `${filePath || ""} ${title || ""}`.toLowerCase();
    if (combined.includes(".ipynb") || combined.includes("notebook") || combined.includes("colab")) {
      return { type: "notebook", label: "Jupyter Notebook", icon: "📓", pillClass: "material-pill-notebook" };
    }
    if (combined.includes("zoom.us") || combined.includes("recording") || combined.includes(".mp4") || combined.includes(".webm") || combined.includes("video")) {
      return { type: "video", label: "Video Recording", icon: "🎥", pillClass: "material-pill-video" };
    }
    if (combined.includes("youtube.com") || combined.includes("youtu.be")) {
      return { type: "youtube", label: "YouTube Video", icon: "▶️", pillClass: "material-pill-video" };
    }
    if (combined.includes("drive.google.com") || combined.includes("docs.google.com")) {
      return { type: "drive", label: "Google Drive", icon: "📁", pillClass: "material-pill-drive" };
    }
    if (combined.includes(".pdf") || combined.includes(".docx") || combined.includes(".pptx") || combined.includes("slide") || combined.includes("document")) {
      return { type: "document", label: "Document", icon: "📄", pillClass: "material-pill-document" };
    }
    return { type: "resource", label: "Resource", icon: "🔗", pillClass: "material-pill-default" };
  };

  // Filter materials based on selected course
  const filteredMaterials = useMemo(() => {
    if (!recapMaterials || recapMaterials.length === 0) return [];
    if (selectedCourseFilter === "all") return recapMaterials;

    return recapMaterials.filter((m) => {
      const cId = m.cohort_id || m.cohort?.id;
      const courseTitle = (m.cohort?.course?.title || m.cohort?.name || "").toLowerCase();
      return (
        String(cId) === String(selectedCourseFilter) ||
        courseTitle.includes(selectedCourseFilter.toLowerCase()) ||
        m.cohort_id === null
      );
    });
  }, [recapMaterials, selectedCourseFilter]);

  // Group materials by week number
  const groupedMaterials = useMemo(() => {
    if (filteredMaterials.length === 0) return {};

    const groups = {};
    filteredMaterials.forEach((material) => {
      const weekNum = material.week_number || "general";
      if (!groups[weekNum]) {
        groups[weekNum] = [];
      }
      groups[weekNum].push(material);
    });

    const sortedWeeks = Object.keys(groups).sort((a, b) => {
      if (a === "general") return 1;
      if (b === "general") return -1;
      return Number(b) - Number(a);
    });

    const sortedGroups = {};
    sortedWeeks.forEach((week) => {
      sortedGroups[week] = groups[week];
    });

    return sortedGroups;
  }, [filteredMaterials]);

  // Filter assignments
  const filteredAssignments = useMemo(() => {
    if (!userAssignments || userAssignments.length === 0) return [];
    if (selectedCourseFilter === "all") return userAssignments;

    return userAssignments.filter((a) => {
      const cId = a.cohort_id || a.cohort?.id;
      const courseTitle = (a.cohort?.course?.title || a.cohort?.name || "").toLowerCase();
      return (
        String(cId) === String(selectedCourseFilter) ||
        courseTitle.includes(selectedCourseFilter.toLowerCase())
      );
    });
  }, [userAssignments, selectedCourseFilter]);

  // Split assignments into pending & completed
  const pendingAssignments = useMemo(() => {
    return filteredAssignments.filter((a) => {
      const s = (a.status || "").toLowerCase();
      return s !== "submitted" && s !== "graded" && s !== "completed";
    });
  }, [filteredAssignments]);

  const completedAssignments = useMemo(() => {
    return filteredAssignments.filter((a) => {
      const s = (a.status || "").toLowerCase();
      return s === "submitted" || s === "graded" || s === "completed";
    });
  }, [filteredAssignments]);

  // Navigate to cohort/enrollment assignments page
  const handleViewAssignments = (cohortId, enrollmentId) => {
    const path = cohortId
      ? `${routePrefix}/progress/cohort/${cohortId}/assignments`
      : `${routePrefix}/progress/enrollment/${enrollmentId}/assignments`;
    navigate(path);
  };

  // Switch to materials tab filtered for this course
  const handleViewCourseMaterials = (cohortId, courseTitle) => {
    if (cohortId) {
      setSelectedCourseFilter(String(cohortId));
    } else if (courseTitle) {
      setSelectedCourseFilter(courseTitle);
    }
    setActiveTab("materials");
  };

  const getStatusBadge = (status) => {
    switch ((status || "").toLowerCase()) {
      case "active":
        return "bg-success-subtle text-success";
      case "completed":
        return "bg-primary-subtle text-primary";
      case "expired":
        return "bg-danger-subtle text-danger";
      default:
        return "bg-warning-subtle text-warning";
    }
  };

  if (progressLoading && enrollments.length === 0) {
    return (
      <div className="dashboard-main-section">
        <div className="wrapper min-vh-100">
          <div className="progress-header-section">
            <Skeleton width={200} height={28} />
            <Skeleton width={300} height={16} className="mt-2" />
          </div>
          <div className="row">
            {[1, 2, 3].map((i) => (
              <div className="col-md-6 col-lg-4 mb-4" key={i}>
                <div className="card h-100 p-4">
                  <Skeleton height={100} />
                  <Skeleton count={3} className="mt-3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (progressError && enrollments.length === 0) {
    return (
      <div className="dashboard-main-section">
        <div className="wrapper min-vh-100">
          <div className="alert alert-danger p-4" role="alert">
            <h4 className="alert-heading">Error Loading Progress</h4>
            <p>{progressError.message || "Failed to load your progress data. Please try again later."}</p>
            <button
              className="btn btn-danger"
              onClick={() => {
                dispatch(fetchUserProgress());
                dispatch(fetchUserEnrolledCohorts());
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-main-section">
      <div className="wrapper min-vh-100">
        {/* Header Section */}
        <div className="progress-header-section">
          <h1 className="progress-page-title">My Progress &amp; Course Materials</h1>
          <p className="progress-page-subtitle">
            Track your learning progress, access class materials, and submit course assignments
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="progress-nav-tabs">
          <button
            className={`progress-tab-btn ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            <Book size={18} />
            Overview
            <span className="tab-badge">{enrollments.length}</span>
          </button>

          <button
            className={`progress-tab-btn ${activeTab === "materials" ? "active" : ""}`}
            onClick={() => setActiveTab("materials")}
          >
            <VideoPlay size={18} />
            Course Materials
            <span className="tab-badge">{recapMaterials.length}</span>
          </button>

          <button
            className={`progress-tab-btn ${activeTab === "assignments" ? "active" : ""}`}
            onClick={() => setActiveTab("assignments")}
          >
            <DocumentText size={18} />
            Assignments
            <span className="tab-badge">{userAssignments.length}</span>
          </button>

          <button
            className={`progress-tab-btn ${activeTab === "pending" ? "active" : ""}`}
            onClick={() => setActiveTab("pending")}
          >
            <Clock size={18} />
            Pending
            <span className="tab-badge">{pendingAssignments.length}</span>
          </button>

          <button
            className={`progress-tab-btn ${activeTab === "completed" ? "active" : ""}`}
            onClick={() => setActiveTab("completed")}
          >
            <Check size={18} />
            Completed
            <span className="tab-badge">{completedAssignments.length}</span>
          </button>
        </div>

        {/* Course Filter Bar (Shown on Materials & Assignments tabs) */}
        {activeTab !== "overview" && enrollments.length > 0 && (
          <div className="course-filter-bar">
            <span className="filter-label">Filter by Course:</span>
            <button
              className={`filter-chip ${selectedCourseFilter === "all" ? "active" : ""}`}
              onClick={() => setSelectedCourseFilter("all")}
            >
              All Enrolled Courses ({enrollments.length})
            </button>
            {enrollments.map((enr) => {
              const isPrivate = enr.class_option === "Private Class";
              const title = isPrivate
                ? (enr.course?.title ?? enr.cohort?.course?.title ?? "Private Class")
                : (enr.cohort?.name || enr.course?.title || "Cohort");
              const filterVal = enr.cohort?.id ? String(enr.cohort.id) : (enr.course?.title || title);

              return (
                <button
                  key={enr.id || enr.enrollment_id || title}
                  className={`filter-chip ${selectedCourseFilter === filterVal ? "active" : ""}`}
                  onClick={() => setSelectedCourseFilter(filterVal)}
                >
                  {title}
                </button>
              );
            })}
          </div>
        )}

        {/* =========================================================
            TAB 1: OVERVIEW
            ========================================================= */}
        {activeTab === "overview" && (
          <>
            {enrollments.length === 0 ? (
              <div className="card">
                <div className="card-body text-center py-5">
                  <Book size={48} className="text-muted mb-3" />
                  <h4>No Courses Enrolled</h4>
                  <p className="text-muted">
                    You haven't enrolled in any courses yet.
                  </p>
                  <Link to={`${routePrefix}/cohorts`} className="btn btn-primary">
                    Browse Courses
                  </Link>
                </div>
              </div>
            ) : (
              <div className="row">
                {enrollments.map((enrollment) => {
                  const isPrivate = enrollment.class_option === "Private Class";
                  const courseTitle = isPrivate
                    ? (enrollment.course?.title ?? enrollment.cohort?.course?.title ?? "Private Class Course")
                    : (enrollment.cohort?.name || enrollment.course?.title || "Cohort Course");
                  const cohortId = enrollment.cohort?.id;
                  const enrollmentId = enrollment.enrollment_id || enrollment.id;
                  const progressVal = Number(enrollment.progress) || 0;

                  return (
                    <div className="col-md-6 col-lg-4 mb-4" key={enrollment.enrollment_id || enrollment.id || courseTitle}>
                      <div className="progress-course-card">
                        <div className="progress-course-body">
                          {/* Course Title & Badges */}
                          <div className="d-flex justify-content-between align-items-start mb-3 gap-2">
                            <div>
                              <h5 className="h6 fw-semibold mb-1 text-dark">
                                {courseTitle}
                              </h5>
                              <p className="text-muted small mb-0">
                                {isPrivate
                                  ? `Private Class ${enrollment.duration ? `• ${enrollment.duration}` : ""}`
                                  : (enrollment.cohort?.name || "Standard Cohort")}
                              </p>
                            </div>
                            <span className={`badge ${getStatusBadge(enrollment.status)}`}>
                              {enrollment.status || "active"}
                            </span>
                          </div>

                          {/* Progress Bar */}
                          <div className="mb-3">
                            <div className="d-flex justify-content-between mb-1">
                              <span className="small text-muted">Progress</span>
                              <span className="small fw-semibold text-dark">
                                {progressVal}%
                              </span>
                            </div>
                            <div className="modern-progress-track">
                              <div
                                className="modern-progress-bar"
                                role="progressbar"
                                style={{ width: `${progressVal}%` }}
                                aria-valuenow={progressVal}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              />
                            </div>
                          </div>

                          {/* Breakdown Stats */}
                          <div className="row g-2 mb-4">
                            <div className="col-4">
                              <div className="metric-chip">
                                <div className="text-muted" style={{ fontSize: "11px" }}>Total</div>
                                <div className="fw-bold" style={{ fontSize: "14px" }}>
                                  {enrollment.breakdown?.total_assignments || 0}
                                </div>
                              </div>
                            </div>
                            <div className="col-4">
                              <div className="metric-chip">
                                <div className="text-muted" style={{ fontSize: "11px" }}>Released</div>
                                <div className="fw-bold text-primary" style={{ fontSize: "14px" }}>
                                  {enrollment.breakdown?.released_assignments || 0}
                                </div>
                              </div>
                            </div>
                            <div className="col-4">
                              <div className="metric-chip">
                                <div className="text-muted" style={{ fontSize: "11px" }}>Completed</div>
                                <div className="fw-bold text-success" style={{ fontSize: "14px" }}>
                                  {enrollment.breakdown?.completed_assignments || 0}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="mt-auto d-flex flex-column gap-2">
                            <button
                              className="btn btn-primary btn-sm d-flex align-items-center justify-content-center"
                              style={{ padding: "0.5rem 1rem", borderRadius: "8px" }}
                              onClick={() => handleViewAssignments(cohortId, enrollmentId)}
                            >
                              <DocumentText size={16} className="me-2" />
                              View Assignments
                              <ArrowRight2 size={14} className="ms-2" />
                            </button>

                            <button
                              className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                              style={{ padding: "0.5rem 1rem", borderRadius: "8px" }}
                              onClick={() => handleViewCourseMaterials(cohortId, courseTitle)}
                            >
                              <VideoPlay size={16} className="me-2" />
                              Course Materials
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* =========================================================
            TAB 2: COURSE MATERIALS
            ========================================================= */}
        {activeTab === "materials" && (
          <div>
            {filteredMaterials.length === 0 ? (
              <div className="card">
                <div className="card-body text-center py-5">
                  <VideoPlay size={44} className="text-muted mb-2" />
                  <h5 className="fw-semibold">No Materials Found</h5>
                  <p className="text-muted small mb-0">
                    {selectedCourseFilter !== "all"
                      ? "No recordings or materials uploaded for this course yet."
                      : "Class recordings, lecture notebooks, and materials will appear here once released."}
                  </p>
                </div>
              </div>
            ) : (
              Object.keys(groupedMaterials).map((week) => (
                <div key={week} className="mb-4">
                  <div className="week-group-header">
                    <span className="week-badge">
                      {week === "general" ? "General Resources" : `Week ${week}`}
                    </span>
                    <span className="text-muted small">
                      {groupedMaterials[week].length} {groupedMaterials[week].length === 1 ? "item" : "items"}
                    </span>
                  </div>

                  <div className="row">
                    {groupedMaterials[week].map((material) => {
                      const resourceType = getResourceType(material.file_path, material.title);
                      const isRecapSlug = Boolean(material.slug);
                      const detailUrl = isRecapSlug
                        ? `${routePrefix}/recap-videos/${material.slug}`
                        : material.file_path;

                      return (
                        <div className="col-md-6 mb-3" key={material.id}>
                          <div className="progress-material-card h-100 d-flex flex-column">
                            <div className="d-flex align-items-start gap-3">
                              <div className={`material-icon-pill ${resourceType.pillClass}`} style={{ width: "42px", height: "42px" }}>
                                <span>{resourceType.icon}</span>
                              </div>
                              <div className="flex-grow-1 overflow-hidden">
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="badge bg-light text-secondary small" style={{ fontSize: "11px" }}>
                                    {resourceType.label}
                                  </span>
                                  <span className="text-muted small">
                                    {formatDate(material.created_at)}
                                  </span>
                                </div>
                                <h4 className="h6 fw-semibold mb-1 mt-1 text-dark" style={{ fontSize: "15px" }}>
                                  {material.title || "Class Material"}
                                </h4>
                                {material.cohort?.name && (
                                  <span className="badge bg-light text-muted border small mb-2">
                                    {material.cohort.name}
                                  </span>
                                )}
                                {material.description && (
                                  <p className="text-muted small mb-3">
                                    {material.description.slice(0, 100)}
                                    {material.description.length > 100 ? "..." : ""}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="mt-auto pt-3 border-top d-flex justify-content-end gap-2">
                              {isRecapSlug ? (
                                <Link
                                  to={detailUrl}
                                  className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                                  style={{ borderRadius: "8px", padding: "0.4rem 0.85rem" }}
                                >
                                  <VideoPlay size={15} />
                                  Watch Recording
                                </Link>
                              ) : (
                                <a
                                  href={material.file_path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                                  style={{ borderRadius: "8px", padding: "0.4rem 0.85rem" }}
                                >
                                  <Eye size={15} />
                                  Open Material
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* =========================================================
            TAB 3: ALL ASSIGNMENTS
            ========================================================= */}
        {activeTab === "assignments" && (
          <div className="card">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="h6 fw-semibold mb-0">
                  <DocumentText size={18} className="me-2 text-primary" />
                  All Assignments ({filteredAssignments.length})
                </h5>
              </div>

              {filteredAssignments.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <DocumentText size={40} className="mb-2 text-muted" />
                  <p className="mb-0">No assignments available for the selected filter.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Assignment</th>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Course</th>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Due Date</th>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Status</th>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Score</th>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAssignments.map((assignment) => {
                        const title =
                          assignment.title ||
                          assignment.assignment?.title ||
                          assignment.description ||
                          assignment.name ||
                          "Course Assignment";
                        const courseName =
                          assignment.cohort?.course?.title ||
                          assignment.cohort?.name ||
                          assignment.course?.title ||
                          "Enrolled Course";
                        const statusLower = (assignment.status || "").toLowerCase();
                        const isSubmitted = statusLower === "submitted";
                        const isGraded = statusLower === "graded";
                        const badgeClass = isSubmitted || isGraded
                          ? "bg-success-subtle text-success border border-success"
                          : "bg-warning-subtle text-warning border border-warning";

                        const templateId = assignment.template_id || assignment.id;
                        const assignmentUrl = `${routePrefix}/progress/assignment/${templateId}`;

                        return (
                          <tr key={assignment.id}>
                            <td>
                              <span className="fw-semibold text-dark">{title}</span>
                              {assignment.week_number && (
                                <span className="badge bg-light text-muted ms-2 small">
                                  Week {assignment.week_number}
                                </span>
                              )}
                            </td>
                            <td className="text-muted small">{courseName}</td>
                            <td className="text-muted small">
                              {formatDate(assignment.due_date || assignment.created_at)}
                            </td>
                            <td>
                              <span
                                className={`badge ${badgeClass}`}
                                style={{ textTransform: "capitalize", borderRadius: "6px" }}
                              >
                                {assignment.status || "Pending"}
                              </span>
                            </td>
                            <td className="fw-semibold">{assignment.score || "-"}</td>
                            <td>
                              <Link
                                to={assignmentUrl}
                                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
                                style={{ padding: "0.35rem 0.75rem", fontSize: "12px", borderRadius: "6px" }}
                              >
                                <Eye size={14} className="me-1" />
                                Details
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* =========================================================
            TAB 4: PENDING ASSIGNMENTS
            ========================================================= */}
        {activeTab === "pending" && (
          <div className="card">
            <div className="card-body p-4">
              <h5 className="h6 fw-semibold mb-3">
                <Clock size={18} className="me-2 text-warning" />
                Pending Assignments ({pendingAssignments.length})
              </h5>

              {pendingAssignments.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <Check size={40} className="text-success mb-2" />
                  <h6 className="fw-semibold text-dark">All caught up!</h6>
                  <p className="text-muted small mb-0">
                    You have no pending assignments waiting for submission.
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Assignment</th>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Course</th>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Due Date</th>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Status</th>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingAssignments.map((assignment) => {
                        const title =
                          assignment.title ||
                          assignment.assignment?.title ||
                          assignment.description ||
                          assignment.name ||
                          "Course Assignment";
                        const courseName =
                          assignment.cohort?.course?.title ||
                          assignment.cohort?.name ||
                          assignment.course?.title ||
                          "Enrolled Course";
                        const templateId = assignment.template_id || assignment.id;
                        const assignmentUrl = `${routePrefix}/progress/assignment/${templateId}`;

                        return (
                          <tr key={assignment.id}>
                            <td>
                              <span className="fw-semibold text-dark">{title}</span>
                              {assignment.week_number && (
                                <span className="badge bg-light text-muted ms-2 small">
                                  Week {assignment.week_number}
                                </span>
                              )}
                            </td>
                            <td className="text-muted small">{courseName}</td>
                            <td className="text-muted small">
                              {formatDate(assignment.due_date || assignment.created_at)}
                            </td>
                            <td>
                              <span className="badge bg-warning-subtle text-warning border border-warning">
                                {assignment.status || "Pending"}
                              </span>
                            </td>
                            <td>
                              <Link
                                to={assignmentUrl}
                                className="btn btn-sm btn-primary d-inline-flex align-items-center"
                                style={{ padding: "0.35rem 0.8rem", fontSize: "12px", borderRadius: "6px" }}
                              >
                                <Archive size={14} className="me-1" />
                                Submit
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* =========================================================
            TAB 5: COMPLETED ASSIGNMENTS
            ========================================================= */}
        {activeTab === "completed" && (
          <div className="card">
            <div className="card-body p-4">
              <h5 className="h6 fw-semibold mb-3">
                <Check size={18} className="me-2 text-success" />
                Completed &amp; Submitted Assignments ({completedAssignments.length})
              </h5>

              {completedAssignments.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <Clock size={40} className="text-muted mb-2" />
                  <p className="text-muted small mb-0">
                    No completed assignments yet. Once you submit and get graded, your records will appear here.
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Assignment</th>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Course</th>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Date</th>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Status</th>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Score</th>
                        <th style={{ color: "#64748b", fontSize: "13px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completedAssignments.map((assignment) => {
                        const title =
                          assignment.title ||
                          assignment.assignment?.title ||
                          assignment.description ||
                          assignment.name ||
                          "Course Assignment";
                        const courseName =
                          assignment.cohort?.course?.title ||
                          assignment.cohort?.name ||
                          assignment.course?.title ||
                          "Enrolled Course";
                        const templateId = assignment.template_id || assignment.id;
                        const assignmentUrl = `${routePrefix}/progress/assignment/${templateId}`;

                        return (
                          <tr key={assignment.id}>
                            <td>
                              <span className="fw-semibold text-dark">{title}</span>
                              {assignment.week_number && (
                                <span className="badge bg-light text-muted ms-2 small">
                                  Week {assignment.week_number}
                                </span>
                              )}
                            </td>
                            <td className="text-muted small">{courseName}</td>
                            <td className="text-muted small">
                              {formatDate(assignment.created_at || assignment.due_date)}
                            </td>
                            <td>
                              <span className="badge bg-success-subtle text-success border border-success">
                                {assignment.status || "Submitted"}
                              </span>
                            </td>
                            <td className="fw-bold text-success">{assignment.score || "-"}</td>
                            <td>
                              <Link
                                to={assignmentUrl}
                                className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center"
                                style={{ padding: "0.35rem 0.75rem", fontSize: "12px", borderRadius: "6px" }}
                              >
                                <Eye size={14} className="me-1" />
                                View Details
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Progress;